import React, { useState, useRef, useEffect } from "react";
import CategoryNavItem from "../CategoryNavItem/CategoryNavItem";

import "./styles.styl";

import ArrowLeftIcon from "jsx:../../../assets/svgs/arrow-left.svg";
import ArrowRightIcon from "jsx:../../../assets/svgs/arrow-right.svg";
import classNames from "classnames";

export default function CategoryNavList({
  productsByCategory,
  handleClick = () => {},
  currentSelected,
  currentSelectedMainCategory,
  categories,
  parentProductsByCategory,
  type,
}) {
  const listRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] =
    useState(currentSelected);
  const [isOverCategoryList, setIsOverCategoryList] = useState(false);

  if (type === "subcategory") {
    useEffect(() => {
      setSelectedSubcategory(currentSelected);
    }, [currentSelectedMainCategory]);
  }

  // More reliable mouse over detection
  const handleMouseEnter = () => {
    setIsOverCategoryList(true);
  };

  const handleMouseLeave = () => {
    setIsOverCategoryList(false);
  };

  // Effect to handle CSS class for disabling vertical scroll
  useEffect(() => {
    const handleGlobalWheel = (event) => {
      const container = listRef.current;
      if (!container) return;
      
      // Check if mouse is actually over the container
      const isActuallyOverContainer = container.contains(event.target) || 
                                     container.parentElement?.contains(event.target);
      
      if (isOverCategoryList || isActuallyOverContainer) {
        const canScrollHorizontally = container.scrollWidth > container.clientWidth;
        // Lower threshold for mouse wheel detection
        const isMouseWheel = Math.abs(event.deltaY) > 10;
        
        if (canScrollHorizontally && isMouseWheel) {
          // Aggressive prevention
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Handle horizontal scrolling
          const scrollAmount = event.deltaY * 1.5;
          container.scrollBy({
            left: scrollAmount,
            behavior: "auto",
          });
          
          return false;
        } else if (canScrollHorizontally && event.shiftKey) {
          // For trackpad, only scroll horizontally when shift is pressed
          const scrollAmount = event.deltaY * 1.2;
          container.scrollBy({
            left: scrollAmount,
            behavior: "auto",
          });
        }
      }
    };
    
    // Use both capture and bubble phases
    document.addEventListener('wheel', handleGlobalWheel, { passive: false, capture: true });
    document.addEventListener('wheel', handleGlobalWheel, { passive: false, capture: false });
    
    return () => {
      document.removeEventListener('wheel', handleGlobalWheel, { capture: true });
      document.removeEventListener('wheel', handleGlobalWheel, { capture: false });
    };
  }, [isOverCategoryList]);

  // Ideally this should come from backend
  // handle translation so if site is in english this is being handled as well.

  const categorieWithoutEmptyArr = Object.keys(productsByCategory ?? {}).filter(
    (i) => {
      return productsByCategory[i] && productsByCategory[i].length !== 0;
    },
  );
  let shouldAppendAll = false;
  if (
    type == "subcategory" &&
    parentProductsByCategory[currentSelectedMainCategory]
  ) {
    const checkIfFirstKeyIsEmptyString =
      Object.keys(parentProductsByCategory[currentSelectedMainCategory])[0] ===
      "";
    if (checkIfFirstKeyIsEmptyString) {
      shouldAppendAll = true;
    }
  }
  const categoriesWithAll =
    categorieWithoutEmptyArr.length > 1 && type === "subcategory"
      ? [...categorieWithoutEmptyArr]
      : categories;

  const handleListClick = (event) => {
    const category = event.target.getAttribute("data-category");
    if (category) {
      setSelectedSubcategory(category);
      handleClick(category);
    }
  };
  const handleScroll = () => {
    const container = listRef.current;

    if (container) {
      const atStart = container.scrollLeft === 0;
      const atEnd =
        Math.ceil(container.scrollLeft + container.clientWidth) >=
        container.scrollWidth;

      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    }
  };

  const scroll = (direction) => {
    const container = listRef.current;
    if (container) {
      const scrollAmount = 150;
      container.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    setSelectedSubcategory(currentSelected);
  }, [currentSelected]);

  // Add effect to recalculate scroll state when content changes
  useEffect(() => {
    // Use a small delay to ensure DOM has updated when content changes
    const timer = setTimeout(() => {
      handleScroll();
    }, 0);
    
    const container = listRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    
    return () => {
      clearTimeout(timer);
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [categoriesWithAll, selectedSubcategory, currentSelectedMainCategory]);

  return (
    <div 
      className="categoryNavList__wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {canScrollLeft && (
        <button
          className="scrollButton scrollButton__left"
          onClick={() => scroll("left")}
        >
          <ArrowLeftIcon aria-label="Scroll left" />
        </button>
      )}
      <div
        className={classNames(
          "categoryNavList__main categoryNavList__manage_space",
          { 
            categoryNavList__subCategory: type === "subcategory",
            categoryNavList__headerCategory: type === "title"
          },
        )}
        ref={listRef}
        onClick={handleListClick}
      >
        {categoriesWithAll &&
          categoriesWithAll.map((category, index) => (
            <CategoryNavItem
              key={`${type}-${category}-${index}`}
              selected={category === selectedSubcategory}
              canScrollLeft={canScrollLeft}
              canScrollRight={canScrollRight}
              type={type}
              content={category}
              dataCategory={category}
            />
          ))}
      </div>
      {canScrollRight && (
        <button
          className="scrollButton scrollButton__right"
          onClick={() => scroll("right")}
        >
          <ArrowRightIcon aria-label="Scroll right" />
        </button>
      )}
    </div>
  );
}
